// hh-be3 (case-35) — EditPropertyScreen.
//
// Rename / re-type a property, or start the cascade-delete flow. Reached from
// the property tree (the pencil button on a property header). Mirrors
// EditItemScreen: there is no GET /api/v1/properties/{id}, so the property is
// located by walking the tree (GET /api/v1/properties/tree). Backend:
//   - PATCH /api/v1/properties/{id}  — name / type
// Delete is intentionally NOT a direct DELETE call: deleting a property
// cascades into its rooms/items/equipment, so the user is routed into the
// destruct preview (destruct_cascade) for an explicit confirmation.
//
// TODO(hh-be3 follow-up): CASE-35 also envisions a "move content to another
// property" alternative instead of cascade-delete; that needs a backend
// bulk-move endpoint and is out of scope here.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Btn, type PropertyType } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { properties, humanizeError } from '../../api';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';
import { usePropertyTree } from './usePropertyTree';
import { FormLabel, FormInput, FormFooter, FormError } from './formPrimitives';
import { PropertyTypeChips } from './PropertyTypeChips';

/** Известные значения Property.type — для сужения сырой строки из дерева. */
const KNOWN_TYPES: readonly PropertyType[] = [
  'apartment',
  'house',
  'dacha',
  'garage',
  'car',
  'other',
];

/** Сужает сырой `type` из дерева к PropertyType; неизвестное/пустое → null. */
function coercePropertyType(raw: string | null | undefined): PropertyType | null {
  if (raw == null) return null;
  return KNOWN_TYPES.includes(raw as PropertyType) ? (raw as PropertyType) : null;
}

export interface EditPropertyScreenProps {
  readonly router: RouterApi;
  readonly propertyId: string;
}

interface LocatedProperty {
  readonly name: string;
  /** Текущий тип собственности (сырая строка из дерева, может быть пустой). */
  readonly type: string;
}

/** Walks the tree to find the property by id. */
function locateProperty(
  tree: readonly PropertyTreeNode[],
  propertyId: string,
): LocatedProperty | null {
  for (const property of tree) {
    if (property.id === propertyId) {
      return { name: property.name, type: property.type };
    }
  }
  return null;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

export function EditPropertyScreen({
  router,
  propertyId,
}: EditPropertyScreenProps): JSX.Element {
  const tree = usePropertyTree();
  const [name, setName] = useState<string>('');
  const [type, setType] = useState<PropertyType | null>(null);
  const [original, setOriginal] = useState<LocatedProperty | null>(null);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the form once from the tree.
  useEffect(() => {
    if (tree.status !== 'ready' || hydrated) return;
    const found = locateProperty(tree.properties, propertyId);
    if (found !== null) {
      setName(found.name);
      setType(coercePropertyType(found.type));
      setOriginal(found);
    }
    setHydrated(true);
  }, [tree.status, tree.properties, propertyId, hydrated]);

  const notFound = hydrated && original === null;

  // Изменилось ли что-то относительно исходного состояния.
  const dirty = useMemo<boolean>(() => {
    if (original === null) return false;
    const nameChanged = name.trim() !== original.name;
    const typeChanged = type !== coercePropertyType(original.type);
    return nameChanged || typeChanged;
  }, [original, name, type]);

  const canSave = useMemo<boolean>(
    () => original !== null && name.trim().length > 0 && dirty && !pending,
    [original, name, dirty, pending],
  );

  const back = (): void => router.back();

  const save = (): void => {
    if (original === null || name.trim().length === 0) return;
    const trimmed = name.trim();
    if (!dirty) {
      router.back();
      return;
    }
    setPending(true);
    setError(null);
    // Шлём только изменившиеся поля — PATCH применяет их частично.
    const patch: { name?: string; type?: string } = {};
    if (trimmed !== original.name) patch.name = trimmed;
    if (type !== coercePropertyType(original.type) && type !== null) patch.type = type;
    properties
      .updateProperty(propertyId, patch)
      .then(() => {
        router.back();
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  // Deleting a property cascades — hand off to the destruct preview screen.
  const remove = (): void => {
    if (pending) return;
    router.go('destruct_cascade', { id: propertyId });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader title="Править собственность" subtitle="Комнаты и история сохранятся" onBack={back} />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        {tree.status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
        )}
        {tree.status === 'error' && tree.error !== null && (
          <FormError message={tree.error} />
        )}
        {notFound && <FormError message="Собственность не найдена — возможно, уже удалена." />}

        {tree.status === 'ready' && original !== null && (
          <>
            <FormLabel style={{ padding: '6px 4px 8px' }}>Название</FormLabel>
            <FormInput value={name} onChange={setName} disabled={pending} />

            <FormLabel style={{ marginTop: 18 }}>Тип</FormLabel>
            <PropertyTypeChips value={type} onChange={setType} disabled={pending} />

            <Btn
              kind="danger"
              icon="trash"
              onClick={remove}
              disabled={pending}
              style={{ marginTop: 18, width: '100%' }}
            >
              Удалить собственность
            </Btn>
            <div style={{ fontSize: 12, color: T.ink3, padding: '8px 2px 0' }}>
              Покажем, что именно удалится вместе с собственностью, прежде чем
              подтвердить.
            </div>

            {error !== null && <FormError message={error} />}
          </>
        )}
      </div>
      <FormFooter
        onCancel={back}
        onConfirm={save}
        confirmLabel="Сохранить"
        confirmIcon="check"
        disabled={!canSave}
        pending={pending}
      />
    </div>
  );
}
